import React, { useRef } from 'react';

export type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
  { value: 'all', label: 'All' },
];

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  className?: string;
}

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({ value, onChange, className }) => {
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (currentIndex + 1) % TIME_RANGE_OPTIONS.length;
      onChange(TIME_RANGE_OPTIONS[next].value);
      const buttons = tabListRef.current?.querySelectorAll('button');
      buttons?.[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (currentIndex - 1 + TIME_RANGE_OPTIONS.length) % TIME_RANGE_OPTIONS.length;
      onChange(TIME_RANGE_OPTIONS[prev].value);
      const buttons = tabListRef.current?.querySelectorAll('button');
      buttons?.[prev]?.focus();
    }
  };

  return (
    <div
      ref={tabListRef}
      role="tablist"
      aria-label="Select time range"
      className={`flex items-center gap-1 ${className ?? ''}`}
    >
      {TIME_RANGE_OPTIONS.map((option, index) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default TimeRangeSelector;
